import TaskService from "@/src/api/TaskService.js";
import { getHighestTaskPriorityEntity } from "@/src/utils/utils.js";
import { taskErrorTypes } from "@/src/constants/taskConstants.js";
import { bannerComponentTypes } from "@/src/constants/bannerConstants.js";

const STORAGE_KEY = "autoPlayState";

const MutationTypes = {
  SET_AUTOPLAY_SESSION_ID: "SET_AUTOPLAY_SESSION_ID",
  SET_AUTOPLAY_INITIALIZATION_STATUS: "SET_AUTOPLAY_INITIALIZATION_STATUS",
  SET_CURRENT_AUTOPLAY_TASK: "SET_CURRENT_AUTOPLAY_TASK",
  SET_TASK_LINKED_ENTITY: "SET_TASK_LINKED_ENTITY",
  SET_TASK_LINKED_ENTITY_DETAILS: "SET_TASK_LINKED_ENTITY_DETAILS",
  RESET_STATE: "RESET_STATE",
  SAVE_STATE: "SAVE_STATE",
  RESTORE_STATE: "RESTORE_STATE",
};

const initialState = {
  autoPlaySessionId: null,
  isInitializingAutoPlay: false,
  currentAutoPlayTask: null,
  taskLinkedEntity: null,
  taskLinkedEntityDetails: null,
};

const persistenceUtils = {
  saveState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error("Error saving state:", error);
    }
  },
  clearState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing state:", error);
    }
  },
};

const state = { ...initialState };

const mutations = {
  [MutationTypes.SET_AUTOPLAY_SESSION_ID](state, id) {
    state.autoPlaySessionId = id;
  },
  [MutationTypes.SET_AUTOPLAY_INITIALIZATION_STATUS](state, status) {
    state.isInitializingAutoPlay = status;
  },
  [MutationTypes.SET_CURRENT_AUTOPLAY_TASK](state, task) {
    state.currentAutoPlayTask = task;
  },
  [MutationTypes.SET_TASK_LINKED_ENTITY](state, entity) {
    state.taskLinkedEntity = entity;
  },
  [MutationTypes.SET_TASK_LINKED_ENTITY_DETAILS](state, details) {
    state.taskLinkedEntityDetails = details;
    persistenceUtils.saveState(state);
  },
  [MutationTypes.RESET_STATE](state) {
    Object.assign(state, { ...initialState });
  },
  [MutationTypes.SAVE_STATE](state) {
    persistenceUtils.saveState(state);
  },
  [MutationTypes.RESTORE_STATE](state, savedState) {
    Object.assign(state, savedState);
  },
};

const actions = {
  initStore({ commit, dispatch }) {
    const savedState = localStorage.getItem(STORAGE_KEY);

    if (savedState) {
      const parsedState = JSON.parse(savedState);
      commit("RESTORE_STATE", parsedState);

      if (parsedState?.autoPlaySessionId && parsedState?.currentAutoPlayTask) {
        dispatch("dynamicBanner/updateShowBanner", true, { root: true });
        dispatch(
          "dynamicBanner/updateBannerComponentType",
          bannerComponentTypes.AUTOPLAY_TASK,
          { root: true },
        );
      }
    }
  },
  async resetLoadingStates({ commit }) {
    commit(MutationTypes.SET_AUTOPLAY_INITIALIZATION_STATUS, false);
  },
  async initializeAutoPlay({ commit, dispatch }, { viewId, taskId }) {
    commit(MutationTypes.SET_AUTOPLAY_INITIALIZATION_STATUS, true);

    try {
      const payload = { view_id: viewId };

      if (taskId) {
        payload.highest_priority_task_id = taskId;
      }

      const response = await TaskService.startTaskQueue(payload);

      if (!response?.success) {
        throw new Error("Failed to initialize task auto-play");
      }

      const isTasksCountZero = response?.total_count === 0;
      if (isTasksCountZero) {
        throw new Error(taskErrorTypes.QUEUE_EMPTY);
      }

      commit(MutationTypes.SET_AUTOPLAY_SESSION_ID, response.session_id);
      commit(MutationTypes.SAVE_STATE);

      return response;
    } catch (error) {
      console.error("Error initializing auto-play:", error);
      await dispatch("resetAutoPlayState");
      throw error;
    } finally {
      commit(MutationTypes.SET_AUTOPLAY_INITIALIZATION_STATUS, false);
    }
  },

  async loadNextAutoPlayTask({ state, dispatch, commit }) {
    try {
      if (!state.autoPlaySessionId) {
        throw new Error("No active auto-play session");
      }

      const response = await TaskService.getNextTaskQueue(
        state.autoPlaySessionId,
      );

      if (!response?.success) {
        throw new Error("Failed to load next task in auto-play");
      }

      const taskLinkedEntity = getHighestTaskPriorityEntity(response?.task);

      commit(MutationTypes.SET_CURRENT_AUTOPLAY_TASK, response);
      commit(MutationTypes.SET_TASK_LINKED_ENTITY, taskLinkedEntity);
      commit(MutationTypes.SAVE_STATE);

      return response;
    } catch (error) {
      console.error("Error loading next task:", error);
      throw error;
    } finally {
    }
  },

  async completeTask({ commit, dispatch, state }) {
    try {
      const currentTaskId = state?.currentAutoPlayTask?.task?.id;

      if (!currentTaskId) {
        throw new Error("Could not find task id to complete");
      }

      const response = await TaskService.completeTask(currentTaskId);

      if (!response?.success) {
        throw new Error("Failed to complete task");
      }

      commit(MutationTypes.SET_CURRENT_AUTOPLAY_TASK, {
        ...state.currentAutoPlayTask,
        task: { ...response?.task },
      });
      commit(MutationTypes.SAVE_STATE);

      return response;
    } catch (error) {
      console.error("Error completing task:", error);
      throw error;
    } finally {
    }
  },

  resetAutoPlayState({ commit }) {
    persistenceUtils.clearState();
    commit(MutationTypes.RESET_STATE);
  },
};

const getters = {
  autoPlaySessionId: (state) => state.autoPlaySessionId,
  isInitializingAutoPlay: (state) => state.isInitializingAutoPlay,
  currentAutoPlayTask: (state) => state.currentAutoPlayTask,
  taskLinkedEntity: (state) => state.taskLinkedEntity,
  taskLinkedEntityDetails: (state) => state.taskLinkedEntityDetails,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
